/* @ngInject */
function QuoteLineFactory($filter, Utils, CostSnapshot, Notification) {
  class QuoteLine {
    constructor(quoteLineData, rates) {
      this.description = null;
      this.amount = null;
      this.unit = null;
      this.price = null;
      this.margin = null;
      this.discount = null;
      this.selectable = false;
      this.from = null;
      this.costs = [];

      if (quoteLineData) this.setData(quoteLineData, rates);
    }

    setData(data, rates) {
      this.description = data.description;
      this.amount = data.amount;
      this.unit = data.unit;
      this.price = data.price;
      this.discount = data.discount;
      this.selectable = data.selectable;
      this.selectedByClient = data.selectedByClient;
      this.from = data.from;
      this.currency = data.currency;
      if (angular.isArray(data.costs)) this._updateCostSnapshots(data.costs);

      this.updateMargin(rates);
    }

    setDataFromTariff(rates, currencyFrom, tariff) {
      this.description = tariff.description;
      this.amount = 1;
      this.discount = 0;
      this.unit = tariff.unit;
      this.price = tariff.price;
      this.from = tariff._id;
      this.costs = [];

      if (currencyFrom !== this.currency) {
        const priceBeforeRecalculate = this.price;
        this.price = Utils.getCurrencyNumberConverted(rates, currencyFrom, this.currency, this.price);
        this._checkPriceExceedMax(priceBeforeRecalculate, this.currency);
      }

      tariff.costs.forEach((tariffCost) => {
        this.addCostSnapshot({
          name: tariffCost.cost.name,
          unit: tariffCost.cost.unit,
          price: tariffCost.cost.price,
          amount: tariffCost.quantity,
          currency: tariffCost.cost.currency,
          comments: tariffCost.cost.comments,
          from: tariffCost.cost._id,
        });
      });

      this.updateMargin(rates);
    }

    updateMargin(rates) {
      this.margin = Utils.calculateMarginFromPrice(this.getTotalCost(rates), this.price || 0) || 0;
    }

    updatePrice(rates) {
      this.price = Utils.calculatePriceFromMargin(this.getTotalCost(rates), this.margin || 0) || 0;
    }

    getPriceWithDiscount() {
      const price = this.price || 0;
      const discount = this.discount || 0;

      return price - (price * discount) / 100;
    }

    getTotal() {
      const amount = this.amount || 0;
      const price = this.price || 0;
      const discount = this.discount || 0;

      return amount * (price - (price * discount) / 100);
    }

    addCostSnapshotFromCost(rates, cost) {
      if (this._containsCost(cost)) return;

      const costSnapshot = new CostSnapshot();
      costSnapshot.setDataFromCost(cost);
      this.costs.push(costSnapshot);

      if (!this.price && !this.margin) {
        this.margin = 0;
        this.updatePrice(rates);
      } else if (!this.price && this.margin) {
        this.updatePrice(rates);
      } else if (this.price && !this.margin) {
        this.updateMargin(rates);
      } else if (this.price && this.margin) {
        this.updatePrice(rates);
      }
    }

    editCostSnapshotFromCost(rates, cost, costSnapshot) {
      let foundFrom = false;
      for (let i = 0; i < this.costs.length; ++i) {
        if (this.costs[i].from === cost._id && cost._id !== null) {
          foundFrom = true;
          this.costs[i].setDataFromCost(cost);
          if (!this.price && !this.margin) {
            this.margin = 0;
            this.updatePrice(rates);
          } else if (!this.price && this.margin) {
            this.updatePrice(rates);
          } else if (this.price && !this.margin) {
            this.updateMargin(rates);
          } else if (this.price && this.margin) {
            this.updatePrice(rates);
          }
        }
      }

      if (!foundFrom) {
        for (let i = 0; i < this.costs.length; ++i) {
          if (
            (!this.costs[i]._id && this.costs[i] === costSnapshot) ||
            (this.costs[i]._id && this.costs[i]._id === costSnapshot._id)
          ) {
            this.costs[i].setDataFromCost(cost);
            if (!this.price && !this.margin) {
              this.margin = 0;
              this.updatePrice(rates);
            } else if (!this.price && this.margin) {
              this.updatePrice(rates);
            } else if (this.price && !this.margin) {
              this.updateMargin(rates);
            } else if (this.price && this.margin) {
              this.updatePrice(rates);
            }
          }
        }
      }
    }

    addCostSnapshot(costSnapshot) {
      this.costs.push(new CostSnapshot(costSnapshot));
    }

    removeCostSnapshot(rates, cost) {
      for (let i = 0; i < this.costs.length; i++) {
        if (this.costs[i] === cost) {
          this.costs.splice(i, 1);
          break;
        }
      }

      if (this.price && this.costs.length > 0) this.updatePrice(rates);
    }

    getTotalCost(rates) {
      if (this.costs.length === 0) return 0;

      return this.costs.reduce((sum, cost) => {
        const costTotal = Number(cost.price) * Number(cost.amount);
        if (cost.currency === this.currency) return sum + costTotal;
        else return sum + Utils.getCurrencyNumberConverted(rates, cost.currency, this.currency, costTotal);
      }, 0);
    }

    getTotalCostTotalTariff(rates) {
      if (this.costs.length === 0) return 0;

      return this.costs.reduce((sum, cost) => {
        const costTotalTariff = Number(cost.price) * Number(cost.amount);
        if (cost.currency === this.currency) return sum + costTotalTariff * Number(this.amount);
        else
          return (
            sum +
            Utils.getCurrencyNumberConverted(rates, cost.currency, this.currency, costTotalTariff) * Number(this.amount)
          );
      }, 0);
    }

    recalculatePrice(rates, oldCurrency, newCurrency) {
      const priceBeforeRecalculate = this.price;
      this.price = Utils.getCurrencyNumberConverted(rates, oldCurrency, newCurrency, this.price);
      this._checkPriceExceedMax(priceBeforeRecalculate, newCurrency);
    }

    containsAnyCost() {
      return this.costs.length > 0;
    }

    _checkPriceExceedMax(priceBeforeRecalculate, newCurrency) {
      if (this.price > 9999999999.99) {
        this.price = priceBeforeRecalculate;
        Notification.error({
          title: "¡Ups!",
          message: $filter("trans")("quoteLineFactory.checkPriceExceedMax.error", [this.description, newCurrency]),
          delay: "persist",
        });
      }
    }

    _updateCostSnapshots(costSnapshotsData) {
      costSnapshotsData.forEach((costSnapshotData, index) => {
        if (this.costs[index]) this.costs[index].setData(costSnapshotData);
        else this.addCostSnapshot(costSnapshotData);
      });

      if (this.costs.length > costSnapshotsData.length) {
        this.costs = this.costs.slice(0, costSnapshotsData.length);
      }
    }

    _containsCost(cost) {
      for (let i = 0; i < this.costs.length; ++i) {
        if (this.costs[i].from === cost._id && cost._id !== null) return true;
      }

      return false;
    }
  }

  return QuoteLine;
}

export default QuoteLineFactory;
